<template>
    <transition name="fade">
        <section id="location-picker" v-if="isOpen">
            <div class="header mb-3 d-flex justify-content-between">
                <div class="click-tutorial d-flex align-items-center">
                    <icon name="click" class="mr-3" />
                    <span class="text-12">{{ $t('Views_Project_Floorplans_Map_1') }}</span>
                    <icon name="double-click" class="mr-3 ml-3" />
                    <span class="text-12">{{ $t('Views_Project_Floorplans_Map_2') }}</span>
                </div>
                <div class="right-buttons">
                    <div @click="isDatesFilterOpen = !isDatesFilterOpen" class="filter d-flex align-items-center mr-3" :class="{ active: isFilterActive }">
                        <icon class="mr-2" name="ci-calendar" :color="isFilterActive ? 'var(--secondary)' : 'white'" scale=".75" />
                        <p class="text-12 mr-2">{{ $t('Filter_7') }}</p>
                        <p class="text-12 text-weight-bold mr-2" :class="[isFilterActive ? 'text-secondary' : 'text-primary-medium-1']">
                            {{ isFilterActive ? $t('Filter_8') : $t('Filter_9') }}
                        </p>
                        <icon name="caret-down" :flip="isDatesFilterOpen ? 'vertical' : null" color="white" />
                    </div>
                    <div v-show="isDatesFilterOpen" class="dates-filter">
                        <ci-filter-date-select class="dates-filter" :dates="project.capture_dates" v-model="selectedDatesArray" />
                    </div>
                    <div @click="resizeViewpoints" class="resize-btn d-flex justify-content-center align-items-center">
                        <icon v-if="getViewpointsSize() == 1" name="ci-resize-circle" color="white" scale=".75" />
                        <icon v-if="getViewpointsSize() == 2" name="ci-resize-circle-plus" color="white" scale="1.25" />
                        <icon v-if="getViewpointsSize() == 3" name="ci-resize-circle-plus-plus" color="white" scale="1.75" />
                    </div>
                </div>
            </div>

            <div class="content">
                <FloorplanCanvas
                    v-if="isMapSidebarOpen"
                    ref="zoomer"
                    :floorplan="project.map.url"
                    :sectors="project.map.sectors"
                    :viewpoints="project.map.viewpoints"
                    :viewpointSize="getViewpointsSize()"
                    :selectedSector="selectedSector.id"
                    :selectedViewpoint="currentViewpoint.id"
                    :hoveredViewpoint="hoveredViewpoint.id"
                    :hoveredSector="hoveredSector.id"
                    :enableViewpointNameTooltip="true"
                    :enableSectorNameTooltip="true"
                    :datesToFilter="selectedDatesArray"
                    @viewpointClick="mapViewpointClick"
                    @viewpointHover="mapViewpointHover"
                    @sectorClick="sectorClick"
                    @sectorDblClick="sectorDoubleClick"
                    @sectorHover="sectorHover"
                />

                <div class="h-100" v-else>
                    <FloorplanCanvas
                        v-if="selectedFloorplan.url"
                        ref="zoomer"
                        :floorplan="selectedFloorplan.url"
                        :viewpoints="selectedFloorplan.viewpoints"
                        :viewpointSize="getViewpointsSize()"
                        :selectedViewpoint="selectedViewpoint.id"
                        :enableViewpointNameTooltip="true"
                        :hoveredViewpoint="hoveredViewpoint.id"
                        :datesToFilter="selectedDatesArray"
                        @viewpointClick="viewpointClick"
                        @viewpointHover="viewpointHover"
                    />
                    <div class="d-flex justify-content-center align-items-center h-100" v-else>
                        <p class="text-16 text-weight-bold">
                            {{ $t('Components_Viewer_Sidebar_Floorplans_FloorplansLocationPicker_1') }}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    </transition>
</template>

<script>
// ====================
// Icons
// ====================
import 'vue-awesome/icons/caret-down'

// ====================
// UI Components
// ====================
import FloorplanCanvas from '@/components/FloorplanCanvas'

// Vuex
import { mapState, mapGetters, mapActions } from 'vuex'

export default {
    props: {
        /**
         * determines if this component is open
         */
        isOpen: {
            type: Boolean,
            default: false,
        },
        /**
         * the entire project
         */
        project: {
            type: Object,
        },
        /**
         * determines if project is a map or not
         */
        isMap: {
            type: Boolean,
        },
        /**
         * current sector of the showing viewpoint
         */
        currentSector: {
            default: -1,
        },
        /**
         * current floorplan of the showing viewpoint
         */
        currentFloorplan: {
            default: -1,
        },
        /**
         * current viewpoint that is showing
         */
        currentViewpoint: {
            default: -1,
        },
        /**
         * sector that user selected (navigation purposes)
         */
        selectedSector: {
            default: -1,
        },
        /**
         * floorplan that user selected (navigation purposes)
         */
        selectedFloorplan: {
            default: -1,
        },
        /**
         * viewpoint that user selected (navigation purposes)
         */
        selectedViewpoint: {
            default: -1,
        },
        /**
         * viewpoint that user is hovering
         */
        hoveredViewpoint: {
            default: -1,
        },
        /**
         * sector that user is hovering
         */
        hoveredSector: {
            default: -1,
        },
        /**
         * determines if only the MapSidebar is open (SectorsSidebar and FloorplansSidebar must be closed to be true)
         */
        isMapSidebarOpen: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        FloorplanCanvas,
    },
    computed: {
        ...mapState(['dateFilterViewer']),
        ...mapGetters('viewpointsSize', ['getById']),
        ...mapGetters('dateFilterViewer', ['getDatesByProjectId']),
        isFilterActive() {
            return this.getDatesByProjectId(this.project.id).length > 0
        },
    },
    data() {
        return {
            isDatesFilterOpen: false,
            selectedDatesArray: [],
        }
    },
    watch: {
        dateFilterViewer: {
            handler() {
                this.selectedDatesArray = this.getDatesByProjectId(this.project.id)
            },
            deep: true,
        },
        selectedDatesArray() {
            this.setDates({ projectId: this.project.id, dates: this.selectedDatesArray })
        },
    },
    methods: {
        ...mapActions('dateFilterViewer', ['setDates']),
        ...mapActions('viewpointsSize', ['addProject', 'addFloorplan']),
        resizeViewpoints() {
            if (this.getViewpointsSize() < 3) {
                this.updateViewpointsSize(this.getViewpointsSize() + 1)
            } else {
                this.updateViewpointsSize(1)
            }
        },
        getViewpointsSize() {
            let projectSizeInfo = this.getById(Number(this.project.id))

            if (projectSizeInfo) {
                if (this.isMap) {
                    if (this.selectedFloorplan.id == this.project.map.id) {
                        return projectSizeInfo.mapSize
                    } else {
                        let fp = projectSizeInfo.floorplans.find((fp) => fp.id == this.selectedFloorplan.id)
                        if (fp) {
                            return fp.size
                        } else {
                            this.updateViewpointsSize()
                        }
                    }
                } else {
                    let fp = projectSizeInfo.floorplans.find((fp) => fp.id == this.selectedFloorplan.id)
                    if (fp) {
                        return fp.size
                    } else {
                        this.updateViewpointsSize()
                    }
                }
            } else {
                this.updateViewpointsSize()
            }

            return 2
        },
        updateViewpointsSize(vpSize = 2) {
            if (this.isMap) {
                if (this.selectedFloorplan.id == this.project.map.id) {
                    this.addProject({ id: this.project.id, mapSize: vpSize })
                } else {
                    this.addFloorplan({ project_id: this.project.id, id: this.selectedFloorplan.id, size: vpSize })
                }
            } else {
                this.addFloorplan({ project_id: this.project.id, id: this.selectedFloorplan.id, size: vpSize })
            }
        },
        // MAP
        mapViewpointClick(viewpoint) {
            let auxVp = this.project.map.viewpoints.find((vp) => {
                return vp.id == viewpoint.id
            })
            this.$emit('mapViewpointClick', auxVp)
        },
        mapViewpointHover(viewpoint) {
            let auxVp = this.project.map.viewpoints.find((vp) => {
                return vp.id == viewpoint.id
            })
            this.$emit('viewpointHover', auxVp)
        },
        sectorClick(sector) {
            let auxSector = this.project.map.sectors.find((sct) => {
                return sct.id == sector.id
            })
            this.$emit('sectorClick', auxSector)
        },
        sectorDoubleClick(sector) {
            let auxSector = this.project.map.sectors.find((sct) => {
                return sct.id == sector.id
            })
            this.$emit('sectorDblClick', auxSector)
        },
        sectorHover(sector) {
            let auxSector = this.project.map.sectors.find((sec) => {
                return sec.id == sector.id
            })
            this.$emit('sectorHover', auxSector)
        },
        // NON MAP
        viewpointClick(viewpoint) {
            let auxVp = this.selectedFloorplan.viewpoints.find((vp) => {
                return vp.id == viewpoint.id
            })
            this.$emit('viewpointClick', auxVp)
        },
        viewpointHover(viewpoint) {
            let auxVp = this.selectedFloorplan.viewpoints.find((vp) => {
                return vp.id == viewpoint.id
            })
            this.$emit('viewpointHover', auxVp)
        },
    },
}
</script>

<style lang="scss" scoped>
@import '@/scss/components/360Viewer/Sidebar/Floorplans/floorplans-location-picker.scss';
</style>
